import { z } from 'zod';
import { OperationIdSchema } from '../../ids';
import { InventorySchema, TransferDirectionSchema } from '../../models/inventory';
import { ItemAmountsSchema, ItemIdSchema, PositiveQuantitySchema } from '../../models/items';
import { API_PREFIX, defineEndpoint } from '../endpoint';

/** A ship's id: its entity's in storage. */
const ShipIdSchema = z.string().min(1).max(64);

/** A ship's hold as its owner sees it (MOVE-SHIP T7): what it keeps and the load it takes. */
export const HoldViewSchema = z.object({
  ship: ShipIdSchema,
  items: ItemAmountsSchema,
  /** Its room in load units (its class's). */
  capacity: z.number().int().min(0),
  /** The dock it is moored at, where it is loaded; null adrift. */
  mooredAt: z.string().nullable(),
});
export type HoldView = z.infer<typeof HoldViewSchema>;

/**
 * Buys a ship of a class from the boatman of a dock (MOVE-SHIP NA-1), standing by the boatman's door:
 * its price goes into the boatman's till, and the ship is launched moored at that dock, the buyer's.
 * Idempotent.
 */
export const buyShip = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/ships`,
  access: 'session',
  body: z.object({
    operationId: OperationIdSchema,
    dock: z.string().min(1).max(64),
    class: z.string().min(1).max(32),
  }),
  response: z.object({
    operationId: OperationIdSchema,
    ship: z.object({ id: ShipIdSchema, class: z.string() }),
    inventory: InventorySchema,
  }),
});

/**
 * Refloats a sunk ship where it sank (MOVE-SHIP CW-9, CW-10 a): its owner, within reach of its wreck
 * while it lies, pays a share of its price; it rises empty, its hull half whole, its cargo left in the
 * wreck. Idempotent.
 */
export const refloatShip = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/ships/:ship/refloat`,
  access: 'session',
  params: z.object({ ship: ShipIdSchema }),
  body: z.object({ operationId: OperationIdSchema }),
  response: z.object({
    operationId: OperationIdSchema,
    ship: z.object({ id: ShipIdSchema, class: z.string() }),
    inventory: InventorySchema,
  }),
});

/**
 * The ships a character owns (MOVE-SHIP), afloat and sunk: what its client offers to refloat at a
 * wreck (CW-10), and where each lies moored.
 */
export const getMyShips = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/ships/mine`,
  access: 'session',
  response: z.object({
    ships: z.array(
      z.object({
        id: ShipIdSchema,
        class: z.string(),
        sunk: z.boolean(),
        mooredAt: z.string().nullable(),
      }),
    ),
  }),
});

/** A ship's hold (MOVE-SHIP T7): its owner's to read. */
export const getHold = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/ships/:ship/hold`,
  access: 'session',
  params: z.object({ ship: ShipIdSchema }),
  response: HoldViewSchema,
});

/**
 * Moves goods between the backpack and a ship's hold (MOVE-SHIP T7, NA-7): only while it is moored
 * at a dock, by its owner standing within the dock's reach. Idempotent.
 */
export const transferHold = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/ships/:ship/hold`,
  access: 'session',
  params: z.object({ ship: ShipIdSchema }),
  body: z.object({
    operationId: OperationIdSchema,
    direction: TransferDirectionSchema,
    item: ItemIdSchema,
    quantity: PositiveQuantitySchema,
  }),
  response: z.object({
    operationId: OperationIdSchema,
    inventory: InventorySchema,
    hold: HoldViewSchema,
  }),
});
